import React, { useState, useRef } from "react";
import PropTypes from "kf-proptypes/widgets";
import { Trans } from "@lingui/macro";

import { Button, BUTTON_TYPES } from "../../inputs/index.jsx";
import {
  KfIcon,
  KF_ICONS,
  ICON_SIZE,
  ICON_STROKE
} from "@widgets/common/components/primitives/kf_icon/index.jsx";
import { copyTextToClipboard } from "@widgets/common/libs/util.js";

import styles from "./copy.css";

export const COPY_TYPES = {
  NORMAL: "normal",
  ICON: "icon",
  EMBEDDED: "embedded",
  MINIMAL: "minimal"
};

export const COPY_SIZES = {
  BASE: "base",
  SMALL: "small"
};

export { copyTextToClipboard };

/**
 * Use this component to copy the content in the clipboard
 */
export function Copy(props) {
  const {
    label,
    content,
    type = COPY_TYPES.NORMAL,
    onCopy,
    size = COPY_SIZES.BASE
  } = props;
  const [showSuccess, setShowSuccess] = useState(false);
  let successTimeoutHandler = useRef(null);

  const handleCopy = () => {
    clearTimeout(successTimeoutHandler.current);
    try {
      copyTextToClipboard(content);
      onCopy && onCopy();
      setShowSuccess(true);
      successTimeoutHandler.current = setTimeout(() => {
        setShowSuccess(false);
      }, 3000);
    } catch (err) {
      console.warn("Copy to clipboard failed", err);
    }
  };

  return (
    <div
      className={`${styles.copy} ${styles[`${size}`]}`}
      data-component="copy"
    >
      {label && <label className={"formlabel"}>{label}</label>}
      <div className={`${styles.copyfield} ${styles[type]}`}>
        {type === COPY_TYPES.MINIMAL ? (
          <div className={styles.wrapper} onClick={handleCopy}>
            <span className="small-400 ellipsis">{content}</span>
            <div className={styles.copyIcon}>
              <KfIcon
                icon={KF_ICONS.COPY}
                size={ICON_SIZE.SIZE_16}
                stroke={ICON_STROKE.REGULAR}
              />
            </div>
          </div>
        ) : (
          <>
            <div className={`${styles.content} base`} onClick={handleCopy}>
              {(type === COPY_TYPES.NORMAL || type === COPY_TYPES.ICON) && (
                <span className="ellipsis">{content}</span>
              )}
              {type !== COPY_TYPES.NORMAL && (
                <div className={styles.copyIcon}>
                  <KfIcon
                    icon={KF_ICONS.COPY}
                    size={ICON_SIZE.SIZE_16}
                    stroke={ICON_STROKE.REGULAR}
                  />
                </div>
              )}
            </div>
            {type === COPY_TYPES.NORMAL && (
              <React.Fragment>
                <Button
                  type={BUTTON_TYPES.TERTIARY}
                  className={styles.copyNormalButton}
                  onClick={handleCopy}
                >
                  <Trans>Copy</Trans>
                </Button>
              </React.Fragment>
            )}
          </>
        )}
        <span
          className={`small600 ${styles.status} ${
            showSuccess ? styles.success : ""
          }`}
        >
          <Trans>Copied</Trans>
        </span>
      </div>
    </div>
  );
}

Copy.propTypes = {
  /** label for the copy */
  label: PropTypes.string,
  /** Content value of the copy */
  content: PropTypes.string.isRequired,
  /** Callback function that is triggered when copy is clicked*/
  onCopy: PropTypes.func,
  /** Different type of copy*/
  type: PropTypes.oneOf(Object.values(COPY_TYPES)),
  /** Size of the copy (Base and small is supported) */
  size: PropTypes.oneOf(Object.values(COPY_SIZES))
};

Copy.defaultProps = {
  type: COPY_TYPES.NORMAL,
  size: COPY_SIZES.BASE
};
